import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { recordChunk, recordCache, emitState } from "@/lib/translateTelemetry";
import { prefetchTranslations } from "@/lib/translatePrefetch";

/**
 * PageTranslator — DOM walker translation with:
 *  - synchronous cache application (no flash)
 *  - viewport-first prioritisation
 *  - parallel chunk processing (progressive reveal)
 *  - telemetry (latency, cache hits)
 *  - UI state events for overlay/skeleton ("translate:state")
 *  - prefetch of nav/first-screen strings on language change
 */

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate`;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE",
  "TEXTAREA", "INPUT", "SVG", "PATH",
]);

const ATTR_ORIGINALS = new WeakMap<Text, string>();
const memCache = new Map<string, string>();

const SOURCE_LANG = "de";
const CHUNK_SIZE = 25;
const PARALLEL_CHUNKS = 4;

const cacheKey = (lang: string, src: string) => `tr::${lang}::${src}`;

const getCached = (lang: string, src: string): string | undefined => {
  const k = `${lang}::${src}`;
  if (memCache.has(k)) return memCache.get(k);
  try {
    const v = localStorage.getItem(cacheKey(lang, src));
    if (v != null) {
      memCache.set(k, v);
      return v;
    }
  } catch {}
  return undefined;
};

const setCached = (lang: string, src: string, translated: string) => {
  memCache.set(`${lang}::${src}`, translated);
  try {
    localStorage.setItem(cacheKey(lang, src), translated);
  } catch {}
};

const isTranslatable = (text: string) => {
  const trimmed = text.trim();
  if (trimmed.length < 2) return false;
  if (/^[\d\s.,:;\-+()/\\€$£%@#*&!?'"|·•–—]+$/.test(trimmed)) return false;
  if (/^https?:\/\//i.test(trimmed)) return false;
  if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(trimmed)) return false;
  if (/^\+?[\d\s\-()]{5,}$/.test(trimmed)) return false;
  return true;
};

const isHidden = (el: Element | null): boolean => {
  while (el) {
    if (el instanceof HTMLElement) {
      if (el.dataset.notranslate === "true") return true;
      if (el.getAttribute("translate") === "no") return true;
    }
    el = el.parentElement;
  }
  return false;
};

const isInViewport = (el: Element | null): boolean => {
  if (!el) return false;
  const rect = (el as HTMLElement).getBoundingClientRect?.();
  if (!rect) return false;
  const vh = window.innerHeight || 800;
  return rect.bottom > -200 && rect.top < vh + 200;
};

const collectTextNodes = (root: Node): Text[] => {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = (node as Text).parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (isHidden(parent)) return NodeFilter.FILTER_REJECT;
      const txt = (node as Text).nodeValue || "";
      if (!isTranslatable(txt)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Node | null;
  while ((n = walker.nextNode())) nodes.push(n as Text);
  return nodes;
};

const collectAttrTargets = (root: Element): { el: Element; attr: string; value: string }[] => {
  const out: { el: Element; attr: string; value: string }[] = [];
  const all = root.querySelectorAll<HTMLElement>("[alt],[title],[placeholder],[aria-label]");
  all.forEach((el) => {
    if (isHidden(el)) return;
    if (SKIP_TAGS.has(el.tagName)) return;
    (["alt", "title", "placeholder", "aria-label"] as const).forEach((a) => {
      const v = el.getAttribute(a);
      if (v && isTranslatable(v)) out.push({ el, attr: a, value: v });
    });
  });
  return out;
};

async function translateBatch(texts: string[], target: string): Promise<string[]> {
  const resp = await fetch(FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ texts, target, source: SOURCE_LANG }),
  });
  if (!resp.ok) throw new Error(`Translate failed ${resp.status}`);
  const data = await resp.json();
  return data.translations as string[];
}

type Entry = { texts: Text[]; attrs: { el: Element; attr: string }[] };

const PageTranslator = () => {
  const { currentLang } = useLanguage();
  const location = useLocation();
  const runIdRef = useRef(0);
  const prevLangRef = useRef<string>(currentLang);

  useEffect(() => {
    const lang = currentLang;
    const myRun = ++runIdRef.current;
    const isLanguageSwitch = prevLangRef.current !== lang;
    prevLangRef.current = lang;

    // Kick off prefetch immediately (non-blocking).
    if (isLanguageSwitch && lang !== SOURCE_LANG) {
      prefetchTranslations(lang).catch(() => {});
    }

    const restoreOriginals = () => {
      const root = document.body;
      const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const parent = (node as Text).parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n: Node | null;
      while ((n = w.nextNode())) {
        const t = n as Text;
        const orig = ATTR_ORIGINALS.get(t);
        if (orig != null && t.nodeValue !== orig) t.nodeValue = orig;
      }
      document.body.querySelectorAll<HTMLElement>("[data-orig-attrs]").forEach((el) => {
        const stored = el.getAttribute("data-orig-attrs");
        if (!stored) return;
        try {
          const map = JSON.parse(stored) as Record<string, string>;
          Object.entries(map).forEach(([attr, val]) => el.setAttribute(attr, val));
        } catch {}
      });
    };

    const run = async () => {
      restoreOriginals();
      if (lang === SOURCE_LANG) {
        emitState(lang, "idle");
        return;
      }

      const root = document.body;
      const textNodes = collectTextNodes(root);
      const attrTargets = collectAttrTargets(root);

      const visibleQueue = new Map<string, Entry>();
      const restQueue = new Map<string, Entry>();
      let hits = 0;
      let misses = 0;

      textNodes.forEach((tn) => {
        const src = tn.nodeValue || "";
        if (!ATTR_ORIGINALS.has(tn)) ATTR_ORIGINALS.set(tn, src);
        const cached = getCached(lang, src);
        if (cached !== undefined) {
          tn.nodeValue = cached;
          hits++;
          return;
        }
        misses++;
        const target = isInViewport(tn.parentElement) ? visibleQueue : restQueue;
        const entry = target.get(src) || { texts: [], attrs: [] };
        entry.texts.push(tn);
        target.set(src, entry);
      });

      attrTargets.forEach(({ el, attr, value }) => {
        const existing = el.getAttribute("data-orig-attrs");
        let map: Record<string, string> = {};
        if (existing) { try { map = JSON.parse(existing); } catch {} }
        if (!(attr in map)) {
          map[attr] = value;
          el.setAttribute("data-orig-attrs", JSON.stringify(map));
        }
        const cached = getCached(lang, value);
        if (cached !== undefined) {
          el.setAttribute(attr, cached);
          hits++;
          return;
        }
        misses++;
        const target = isInViewport(el) ? visibleQueue : restQueue;
        const entry = target.get(value) || { texts: [], attrs: [] };
        entry.attrs.push({ el, attr });
        target.set(value, entry);
      });

      recordCache(lang, hits, misses);

      const totalToFetch = visibleQueue.size + restQueue.size;
      if (totalToFetch === 0) {
        emitState(lang, "done");
        return;
      }

      // Total chunks across both queues for progress reporting.
      const totalChunks =
        Math.ceil(visibleQueue.size / CHUNK_SIZE) +
        Math.ceil(restQueue.size / CHUNK_SIZE);
      let doneChunks = 0;

      emitState(lang, "loading", { done: 0, total: totalChunks });

      const processQueue = async (queue: Map<string, Entry>) => {
        const sources = Array.from(queue.keys());
        if (sources.length === 0) return;
        const chunks: string[][] = [];
        for (let i = 0; i < sources.length; i += CHUNK_SIZE) {
          chunks.push(sources.slice(i, i + CHUNK_SIZE));
        }

        const applyChunk = async (chunk: string[]) => {
          if (runIdRef.current !== myRun) return;
          const start = performance.now();
          try {
            const translations = await translateBatch(chunk, lang);
            const latency = performance.now() - start;
            recordChunk(lang, chunk.length, latency, true);
            if (runIdRef.current !== myRun) return;
            chunk.forEach((src, idx) => {
              const tr = translations[idx] ?? src;
              setCached(lang, src, tr);
              const entry = queue.get(src);
              if (!entry) return;
              entry.texts.forEach((t) => { if (t.isConnected) t.nodeValue = tr; });
              entry.attrs.forEach(({ el, attr }) => { if (el.isConnected) el.setAttribute(attr, tr); });
            });
          } catch (e) {
            recordChunk(lang, chunk.length, performance.now() - start, false);
            console.error("Translation chunk failed", e);
          } finally {
            doneChunks++;
            if (runIdRef.current === myRun) {
              emitState(lang, "loading", { done: doneChunks, total: totalChunks });
            }
          }
        };

        let cursor = 0;
        const next = async (): Promise<void> => {
          while (cursor < chunks.length) {
            if (runIdRef.current !== myRun) return;
            const idx = cursor++;
            await applyChunk(chunks[idx]);
          }
        };
        const workers: Promise<void>[] = [];
        for (let i = 0; i < Math.min(PARALLEL_CHUNKS, chunks.length); i++) {
          workers.push(next());
        }
        await Promise.all(workers);
      };

      try {
        await processQueue(visibleQueue);
        if (runIdRef.current === myRun) await processQueue(restQueue);
        if (runIdRef.current === myRun) {
          emitState(lang, "done", { done: totalChunks, total: totalChunks });
        }
      } catch {
        if (runIdRef.current === myRun) emitState(lang, "error");
      }
    };

    run();
    const timer = window.setTimeout(run, 80);

    const observer = new MutationObserver(() => {
      if (runIdRef.current !== myRun) return;
      window.clearTimeout((observer as any)._t);
      (observer as any)._t = window.setTimeout(() => {
        if (runIdRef.current === myRun) run();
      }, 300);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: false });

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [currentLang, location.pathname]);

  return null;
};

export default PageTranslator;
